import httpClient from '~/lib/http-client';

// ─── Types ───

export type AffiliateStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
export type CommissionStatus = 'earned' | 'void';

export interface Affiliate {
  id: string;
  userId: string;
  code: string | null;
  status: AffiliateStatus;
  commissionRateBps: number;
  windowMonths: number;
  promoChannels: string | null;
  audienceNote: string | null;
  websiteUrl: string | null;
  youtubeUrl: string | null;
  instagramUrl: string | null;
  appliedAt: string;
  reviewedAt: string | null;
  createdAt: string;
}

export interface AffiliateCommission {
  id: string;
  paymentKind: string;
  amountCents: number;
  commissionCents: number;
  currency: string;
  status: CommissionStatus;
  accruedAt: string;
  paidAt: string | null;
}

export type PayoutStatus = 'requested' | 'approved' | 'paid' | 'rejected';
export type PayoutMethod = 'bank_in' | 'upi';

/** Where a partner is paid. A tagged union — fields depend on the method. */
export type PayoutDetails =
  | { method: 'bank_in'; accountName: string; accountNumber: string; ifsc: string; bankName?: string }
  | { method: 'upi'; upiId: string; accountName?: string };

export interface AffiliatePayout {
  id: string;
  amountCents: number;
  currency: string;
  status: PayoutStatus;
  requestedAt: string;
  paidAt: string | null;
  payoutRef: string | null;
  note: string | null;
}

export interface AffiliateBalance {
  /** Free to withdraw right now. */
  availableCents: number;
  /** Accrued but still inside the hold window. */
  clearingCents: number;
  /** Sitting in an open withdrawal request. */
  inReviewCents: number;
  /** Settled and sent. */
  paidOutCents: number;
  /** Everything ever earned, void excluded. */
  lifetimeCents: number;
  minPayoutCents: number;
  holdDays: number;
}

export interface AffiliateDashboard {
  affiliate: Affiliate | null;
  funnel?: { clicks: number; signups: number; payingCustomers: number };
  recentCommissions?: AffiliateCommission[];
  balance?: AffiliateBalance;
  payoutDetails?: PayoutDetails | null;
  payouts?: AffiliatePayout[];
}

export interface ApplyAffiliateInput {
  promoChannels?: string;
  audienceNote?: string;
  websiteUrl?: string;
  youtubeUrl?: string;
  instagramUrl?: string;
}

// ─── Calls ───

/** Current user's affiliate status + (if approved) dashboard. */
export async function apiGetMyAffiliate(): Promise<AffiliateDashboard> {
  const { data } = await httpClient.get<AffiliateDashboard>('/api/affiliates/me');
  return data;
}

/** Apply to the affiliate program. */
export async function apiApplyAffiliate(input: ApplyAffiliateInput): Promise<{ affiliate: Affiliate }> {
  const { data } = await httpClient.post<{ affiliate: Affiliate }>('/api/affiliates/apply', input);
  return data;
}

/** Record a click on a referral link (/r/:code). Public — best-effort. */
export async function apiTrackAffiliateClick(code: string, landingPath?: string): Promise<void> {
  await httpClient.post('/api/affiliates/track', { code, landingPath });
}

/** Save where I want to be paid. Replaces any previous details. */
export async function apiSavePayoutDetails(details: PayoutDetails): Promise<{ payoutDetails: PayoutDetails }> {
  const { data } = await httpClient.put<{ payoutDetails: PayoutDetails }>('/api/affiliates/payout-details', details);
  return data;
}

/** Request a withdrawal of a chosen amount, from the minimum up to the balance. */
export async function apiRequestPayout(amountCents: number): Promise<{ payout: AffiliatePayout }> {
  const { data } = await httpClient.post<{ payout: AffiliatePayout }>('/api/affiliates/payouts', { amountCents });
  return data;
}

export interface AffiliateReferral {
  /** Masked — a partner sees the conversion, not the customer's address. */
  email: string;
  referredAt: string;
  windowEndsAt: string;
  earnedCents: number;
  payments: number;
}

/** Customers I referred, newest first. */
export async function apiGetMyReferrals(): Promise<AffiliateReferral[]> {
  const { data } = await httpClient.get<{ referrals: AffiliateReferral[] }>('/api/affiliates/referrals');
  return data.referrals;
}
